'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Logomark } from '@/components/brand'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { AUTO_NAME, isNavItemActive } from '@/lib/navigation'
import { useVisibleNavSections } from '@/components/layout/Sidebar'
import { useNavBadges } from '@/components/layout/useNavBadges'
import { badgeText } from '@/lib/review-view'

interface NavLinkProps {
  href: string
  icon: React.ElementType
  children: React.ReactNode
  onClick?: () => void
  badge?: number
}

function NavLink({ href, icon: Icon, children, onClick, badge }: NavLinkProps) {
  const pathname = usePathname()
  const isActive = isNavItemActive(pathname, href)
  const count = badgeText(badge ?? 0)

  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-xl px-4 py-3',
        'text-base font-medium transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <Icon
        strokeWidth={1.5}
        aria-hidden='true'
        className={cn(
          'h-5 w-5 shrink-0',
          isActive ? 'text-primary-foreground' : 'text-muted-foreground'
        )}
      />
      <span>{children}</span>
      {count && (
        <span className='ml-auto rounded-full bg-brand-cornflower/25 px-2 py-0.5 text-xs font-semibold text-foreground'>
          {count}
          <span className='sr-only'> unseen</span>
        </span>
      )}
    </Link>
  )
}

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const navItems = useVisibleNavSections()
  const badges = useNavBadges()
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side='left' className='w-72 p-0 md:hidden'>
        {/* Header */}
        <SheetHeader className='flex h-20 flex-row items-center justify-between border-b border-border px-4'>
          <Link
            href='/'
            className='flex items-center gap-3'
            onClick={onClose}
          >
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-soft'>
              <Logomark variant='light' size={24} />
            </div>
            <div className='flex flex-col'>
              <SheetTitle className='font-display text-lg font-bold tracking-tight text-foreground'>
                Supervity
              </SheetTitle>
              <span className='text-xs font-medium uppercase leading-tight tracking-widest text-muted-foreground'>
                {AUTO_NAME}
                <br />
                Command Center
              </span>
            </div>
          </Link>
        </SheetHeader>

        {/* Navigation */}
        <nav
          className='flex-1 overflow-y-auto p-4'
          aria-label='Mobile navigation'
        >
          {navItems.map((section, sectionIndex) => (
            <div
              key={section.title}
              className={cn(sectionIndex > 0 && 'mt-6')}
            >
              <p className='mb-2 px-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
                {section.title}
              </p>
              <div className='space-y-1'>
                {section.items.map((item) => (
                  <NavLink
                    key={item.href}
                    href={item.href}
                    icon={item.icon}
                    onClick={onClose}
                    badge={item.badge ? badges[item.badge] : undefined}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  )
}

